import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Renderer2,
  ViewChild,
  OnDestroy,
  input,
  signal,
  ChangeDetectorRef
} from "@angular/core";
import { IonicModule, ModalController, ToastController } from "@ionic/angular";
import { Observable, Subject, Subscription, takeUntil } from "rxjs";
import { GmapService } from "../../shared/services/gmap.service";
import { ModeService } from "../../utils/mode.service";
import { Store } from "@ngrx/store";
import { deleteTracking, upsertTracking } from "../ngrx/tracking.actions";
import { LocationTrackingDto } from "../interfaces/location_tracking";
import {
  selectIsTracking,
  selectTrackingByAppointmentId,
} from "../ngrx/tracking.selectors";
import { CommentDialogComponent } from "../comment-dialog/comment-dialog.component";
import {
  BackgroundGeolocationPlugin,
} from "@capacitor-community/background-geolocation";
import { registerPlugin } from "@capacitor/core";
import { RealTimeService } from "src/app/supabase/services/realtime.service";
import { RealtimeChannel } from "@supabase/supabase-js";
const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>(
  "BackgroundGeolocation"
);

@Component({
    selector: "app-maps",
    templateUrl: "./maps.component.html",
    styleUrls: ["./maps.component.scss"],
    imports: [IonicModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapsComponent implements OnDestroy {
  readonly appointment_id = input<number>(undefined);
  readonly duration = input<number>(undefined);
  @ViewChild("map") mapElementRef: ElementRef;
  googleMaps: any;
  source: any = { lat: 47.36667, lng: 8.55 };
  source_marker: any;
  polyline_path: any;
  map: any;
  directionsService: any;
  directionsDisplay: any;
  speed = null;
  trackings$: Observable<LocationTrackingDto[]>;
  isTracking$ = this.store.select(selectIsTracking);
  latest_tracking: LocationTrackingDto;
  path: any;
  markers: any;
  markerElements = [];
  addingMarker: any;
  isLoading = signal(false);

  private destroy$ = new Subject<void>();
  private locationSubscription: Subscription;

  private realTimeSubscription: RealtimeChannel;

  constructor(
    public maps: GmapService,
    private renderer: Renderer2,
    public modeService: ModeService,
    private modalController: ModalController,
    private toastController: ToastController,
    private store: Store,
    private gmapService: GmapService,
    private realtimeService: RealTimeService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.isLoading.set(true);
    this.trackings$ = this.store.select(
      selectTrackingByAppointmentId(this.appointment_id())
    );

    this.trackings$.pipe(takeUntil(this.destroy$)).subscribe((trackings) => {
      if (trackings.length > 0) {
        this.locationSubscription?.unsubscribe();
        // only add trackings with comment = null to the path
        this.path = trackings.filter((tracking) => {
          return tracking.comment === null;
        });
        this.markers = trackings.filter((tracking) => {
          return tracking.comment !== null;
        });
        this.drawPath(this.path);
        this.initializeMarkers(this.markers);
        this.setMapToLatestTracking();
        this.isLoading.set(false);
      } else {
        // Subscribe to continuous location updates
        this.locationSubscription = this.gmapService.location$.subscribe(
          (location) => {
            if (location && this.map) {
              this.map.setCenter({
                lat: location.latitude,
                lng: location.longitude,
              });
              this.source_marker.setPosition({
                lat: location.latitude,
                lng: location.longitude,
              });
            }
          }
        );
      }
      this.cdr.markForCheck();
    });

  }

  ionViewDidEnter() {
    this.realTimeSubscription = this.realtimeService.connectToTracking(
      this.appointment_id()
    );
    this.realTimeSubscription.subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.mapElementRef.nativeElement.remove();
    this.gmapService.removeGoogleMapScript();
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
  }

  ionViewDidLeave() {
    this.destroy$.next();
    this.destroy$.complete();
    this.mapElementRef.nativeElement.remove();
    this.gmapService.removeGoogleMapScript();
    this.realTimeSubscription.unsubscribe();
  }

  async ngAfterViewInit() {
    await this.loadMap();
    this.initializeMarkers(this.markers);
    if (!this.path || this.path?.length === 0) {
      await this.setToCurrentLocation();
      this.isLoading.set(false);
    } else if (this.path?.length > 0) {
      this.setMapToLatestTracking();
      this.map.setZoom(17);
      this.isLoading.set(false);
    } else {
      this.toastController.create({
        message: "Zugriff auf Standortdienste erforderlich",
        duration: 2000,
      });
      this.isLoading.set(false);
    }
  }

  initializeMarkers(trackings: LocationTrackingDto[]) {
    if (this.markerElements) {
      this.markerElements.forEach((marker) => {
        marker.setMap(null);
      });
      this.markerElements = [];
    }

    if (this.addingMarker) {
      this.addingMarker.setMap(null);
      this.addingMarker = null;
    }

    if (!this.googleMaps || !this.map) return;

    trackings?.forEach((tracking) => {
      const marker = new this.googleMaps.Marker({
        map: this.map,
        position: { lat: tracking.lat, lng: tracking.lng },
        title: "Marker",
        draggable: true,
      });

      marker.addListener("dragend", (event) => {
        const updatedTracking = {
          ...tracking,
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };

        this.store.dispatch(upsertTracking({ tracking: updatedTracking }));
      });

      marker.addListener("click", () => {
        this.openCommentDialog(tracking);
      });

      this.markerElements.push(marker);
    });
  }

  openCommentDialog(tracking: LocationTrackingDto) {
    this.modalController
      .create({
        component: CommentDialogComponent,
        breakpoints: [0, 0.8, 1],
        initialBreakpoint: 0.8,
        componentProps: {
          comment: signal(tracking.comment),
        },
      })
      .then((modal) => {
        modal.onDidDismiss().then((data) => {
          if (data.role === "delete") {
            this.store.dispatch(deleteTracking({ tracking: tracking }));
          }
          if (data.data) {
            const updatedTracking = {
              ...tracking,
              comment: data.data,
            };
            this.store.dispatch(
              upsertTracking({ tracking: updatedTracking })
            );
          }
        });
        return modal.present();
      });
  }

  setMapToLatestTracking() {
    // get latest tracking point based on created_at
    let latest_tracking = this.path.reduce((prev, current) => {
      return prev.created_at > current.created_at ? prev : current;
    });

    if (this.latest_tracking === latest_tracking || !this.map) return;
    this.latest_tracking = latest_tracking;

    this.map?.setCenter({
      lat: this.latest_tracking.lat,
      lng: this.latest_tracking.lng,
    });
    this.source_marker?.setPosition({
      lat: this.latest_tracking.lat,
      lng: this.latest_tracking.lng,
    });
  }

  async loadMap() {
    try {
      let googleMaps: any = await this.maps.loadGoogleMaps();
      this.googleMaps = googleMaps;
      const mapEl = this.mapElementRef.nativeElement;
      this.map = new googleMaps.Map(mapEl, {
        center: this.source,
        disableDefaultUI: true,
        zoom: 13,
      });

      googleMaps.event.addListener(this.map, "click", (event) => {
        this.addingMarker = new googleMaps.Marker({
          map: this.map,
          position: event.latLng,
          title: "Marker",
          draggable: true,
        });

        let location_tracking = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
          appointment_id: this.appointment_id(),
          comment: null,
        } as LocationTrackingDto;

        this.modalController
          .create({
            component: CommentDialogComponent,
            breakpoints: [0, 0.5, 1],
            initialBreakpoint: 0.5,
          })
          .then((modal) => {
            modal.onDidDismiss().then((data) => {
              if (data.data) {
                location_tracking.comment = data.data;
                  this.store.dispatch(
                    upsertTracking({ tracking: location_tracking })
                  );
              } else {
                this.addingMarker.setMap(null);
              }
            });
            return modal.present();
          });
      });

      this.directionsService = new googleMaps.DirectionsService();
      this.directionsDisplay = new googleMaps.DirectionsRenderer();

      const sourceIconUrl = "assets/imgs/car.png";

      const source_icon = {
        url: sourceIconUrl,
        scaledSize: new googleMaps.Size(35, 35), // scaled size
        origin: new googleMaps.Point(0, 0), // origin
        anchor: new googleMaps.Point(0, 0), // anchor
      };

      this.source_marker = new googleMaps.Marker({
        map: this.map,
        position: null,
        animation: googleMaps.Animation.DROP,
        icon: source_icon,
      });

      this.polyline_path = new googleMaps.Polyline({
        map: this.map,
        path: null,
        clickable: false,
        strokeColor: "#FF0000",
        strokeOpacity: 1.0,
        strokeWeight: 3,
      });

      this.renderer.addClass(mapEl, "visible");
      this.drawPath(this.path);
    } catch (e) {
      throw e;
    }
  }

  async setToCurrentLocation() {
    this.isLoading.set(true);
    const coordinates = await this.gmapService.getBackgroundLocation();
    if (coordinates) {
      this.map.setCenter({
        lat: coordinates.latitude,
        lng: coordinates.longitude,
      });
      this.source_marker.setPosition({
        lat: coordinates.latitude,
        lng: coordinates.longitude,
      });
      this.map.setZoom(17);
    } else {
      this.toastController
        .create({
          message:
            "Dein Standort konnte nicht ermittelt werden. Bitte versuche es später erneut und stelle sicher, dass die App Zugriff auf deinen Standort hat.",
          duration: 2000,
        })
        .then((toast) => toast.present());
    }
    this.isLoading.set(false);
  }

  async cancel() {
    if ((await this.modalController.getTop()) !== undefined) {
      this.modalController.dismiss(true);
    }
  }

  async startTracking() {
    try {
      this.maps.startTracking(this.appointment_id(), this.duration());
    } catch {
      this.toastController
        .create({
          message: "Zugriff auf Standortdienste erforderlich",
          duration: 2000,
        })
        .then((toast) => toast.present());
    }
  }

  async stopTracking() {
    this.maps.stopTracking(this.appointment_id());
  }

  async drawPath(path) {
    if (path && this.polyline_path) {
      let mapsPath = path.map((location) => {
        return {
          lat: location.lat,
          lng: location.lng,
        };
      });

      mapsPath = mapsPath.filter((location) => {
        return location.lat && location.lng;
      });

      // Directly set the path without calling snapToRoads
      this.polyline_path.setPath(mapsPath);
    }
  }

  sortByClosestPoints(path: LocationTrackingDto[]): LocationTrackingDto[] {
    if (!path.length) return [];

    const sortedPath = [];
    const remainingPoints = [...path];

    // Start with the first point
    let currentPoint = remainingPoints.shift();
    sortedPath.push(currentPoint);

    while (remainingPoints.length) {
      // Find the closest point to the current point
      let nearestIndex = -1;
      let nearestDistance = Infinity;

      remainingPoints.forEach((point, index) => {
        const distance = this.calculateDistance(
          currentPoint.lat,
          currentPoint.lng,
          point.lat,
          point.lng
        );

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      // Move the closest point to the sortedPath
      currentPoint = remainingPoints.splice(nearestIndex, 1)[0];
      sortedPath.push(currentPoint);
    }

    return sortedPath;
  }

  calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const R = 6371; // Radius of the Earth in km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLng = this.deg2rad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) *
        Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km
    return distance;
  }

  deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }
}
